import { PATTERN_EXPLAINERS } from './pattern-catalog-schemas'

/**
 * One row in the "Leave rhythm" explainer legend surfaced by the home-card
 * info tooltip. This is pure UI content — a plain-language description of what a
 * pattern watches for, with no threshold numbers (those are engine internals in
 * ai.pattern_catalog, not something the employee needs quoted at them).
 */
export interface RhythmLegendItem {
  /** Presentational emoji affordance for the pattern. */
  icon: string
  /** Short, friendly pattern name. */
  label: string
  /** Plain-English description of what the pattern watches for. */
  description: string
  /** Whether the pattern can feed a performance conversation (vs. a pure
   *  wellbeing signal) — drives the ⚠️ note. Curated UI classification. */
  performanceRisk: boolean
}

/**
 * Curated subset of patterns shown in the home-card explainer, each paired with
 * a presentational emoji, a short label, and the wellbeing/performance
 * classification. Order, icon, and label are deliberate UI choices; the
 * description is pulled from the shared pattern explainers (keyed by detector
 * code) so the copy stays in one place. No doc dependency, no threshold numbers.
 */
const CURATED_PATTERNS: ReadonlyArray<{
  icon: string
  code: string
  label: string
  performanceRisk: boolean
}> = [
  { icon: '🌡️', code: 'frequent_sick_leave', label: 'Frequent sick leave', performanceRisk: false },
  { icon: '⚡', code: 'poor_planning_emergency', label: 'Poor planning', performanceRisk: true },
  { icon: '📅', code: 'long_weekend_pattern', label: 'Long-weekend pattern', performanceRisk: false },
  {
    icon: '📊',
    code: 'high_early_utilisation',
    label: 'High early utilisation',
    performanceRisk: false,
  },
]

/**
 * Build the Leave-rhythm explainer legend. A curated pattern whose code has no
 * shared explainer is skipped (rather than rendered with a blank description),
 * so a renamed detector code degrades gracefully.
 */
export function buildRhythmLegend(): RhythmLegendItem[] {
  return CURATED_PATTERNS.flatMap(({ icon, code, label, performanceRisk }) => {
    const explainer = PATTERN_EXPLAINERS[code]
    if (!explainer) return []
    return [{ icon, label, description: explainer.detects, performanceRisk }]
  })
}
